/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { MultiSelect, Option } from 'react-multi-select-component';
import { useHistory } from 'react-router-dom';

import GetAllLanguagesResponse, { LanguageResponse } from '../../../../Api/CRM/Responses/LanguagesResponse';
import CasinoCRMApi from '../../../../casino/Api/CRM/CasinoCRMApi';
import CasinoGameCategory, { CasinoGameCategorySortingType } from '../../../../casino/Api/Models/CasinoGameCategory';
import CasinoGameCategoryTranslation from '../../../../casino/Api/Models/CasinoGameCategoryTranslation';
import Loader from '../../../elements/Loader';
import FormBoolean from '../../../elements/formElements/FormBoolean';
import FormGroupText from '../../../elements/formElements/FormGroupText';
import Select from '../../../elements/formElements/Select';
import { CASINO_GAME_CATEGORY } from '../Helpers/CrmUrls';
import { slugify } from '../Helpers/StringHelper';

import LoadingButton from 'crm/elements/common/LoadingButton';

export interface GameCategoryTranslationsError {
    name?: string;
    slug?: string;
    header?: string;
    language_id?: string;
}

interface Props {
    gameCategoryId?: number;
    handleSubmit: (gameCategory: CasinoGameCategory, onFinish: () => void) => void;
    errors?: any;
    translationErrors?: GameCategoryTranslationsError[];
}

function GameCategoryForm({ gameCategoryId = 0, handleSubmit, errors = {}, translationErrors = [] }: Props) {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [selectGameTypes, setSelectGameTypes] = useState<Option[]>([]);
    const [languages, setLanguages] = useState<GetAllLanguagesResponse>([]);
    const sortingTypeOptions = Object.values(CasinoGameCategorySortingType).reduce((options, value) => {
        options[value] = value;
        return options;
    }, {});
    const [gameCategory, setGameCategory] = useState<CasinoGameCategory>(
        new CasinoGameCategory({
            allowed_types: [],
            is_live_only: false,
            is_visible_on_homepage: true,
            is_all_games: false,
            casino_game_category_translations: [],
            sorting_type: CasinoGameCategorySortingType.DEFAULT,
        })
    );

    const getLanguages = async () => {
        setLanguages(await CasinoCRMApi.languages.getAll());
    };

    const getGameTypesForSelect = async () => {
        const gameTypes = await CasinoCRMApi.games.getAllTypes();
        const selectGamesTypes = gameTypes.map((type: string) => {
            return { label: type, value: type };
        });
        setSelectGameTypes(selectGamesTypes);
    };

    const getGameCategory = (id: number) => {
        CasinoCRMApi.gameCategories
            .getDataById(id)
            .then((gameCategoryFromApi: CasinoGameCategory) => {
                setGameCategory(gameCategoryFromApi);
            })
            .catch((error) => {
                if (error.response.status === 404) {
                    history.push({
                        pathname: CASINO_GAME_CATEGORY,
                    });
                }
            });
    };

    useEffect(() => {
        getGameTypesForSelect();
        getLanguages();

        if (gameCategoryId !== 0) {
            getGameCategory(gameCategoryId);
        }

        setIsLoading(false);
    }, []);

    const handleTranslationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const languageId = parseInt(e.target.getAttribute('data-language-id') || '0');

        const existingTranslation = gameCategory.casino_game_category_translations.find(
            (translation: CasinoGameCategoryTranslation) => translation.language_id === languageId
        );

        const updatedTranslation = {
            ...existingTranslation,
            [name]: name === 'slug' ? slugify(value) : value,
            language_id: languageId,
        };

        const updatedTranslations = gameCategory.casino_game_category_translations.map(
            (translation: CasinoGameCategoryTranslation) => {
                return translation.language_id === languageId ? updatedTranslation : translation;
            }
        );

        if (!existingTranslation) {
            updatedTranslations.push(updatedTranslation);
        }

        setGameCategory(
            new CasinoGameCategory({
                ...gameCategory,
                casino_game_category_translations: updatedTranslations as CasinoGameCategoryTranslation[],
            })
        );
    };

    const handleMultiChange = (e: any) => {
        setGameCategory(
            new CasinoGameCategory({
                ...gameCategory,
                allowed_types: Array.isArray(e) ? e.map((option) => option.value) : [],
            })
        );
    };

    const handleBooleanFieldChange = (fieldName: string, value: string | number) => {
        setGameCategory(new CasinoGameCategory({ ...gameCategory, [fieldName]: value === 1 || value === '1' }));
    };

    const handleFieldChange = (fieldName: string, value: any) => {
        setGameCategory(new CasinoGameCategory({ ...gameCategory, [fieldName]: value }));
    };

    const handleSave = (gameCategoryToSave) => {
        setIsSaving(true);
        // We are passing callback to unlock save button after response is processed
        handleSubmit(gameCategoryToSave, () => {
            setIsSaving(false);
        });
    };

    function getTranslationError(languageId: number, field: string) {
        if (translationErrors) {
            const foundError = translationErrors.find((error: any) => parseInt(error.language_id) === languageId);

            if (foundError) {
                return foundError[field];
            }
        }

        return undefined;
    }

    const getContent = () => {
        return (
            <div className="card">
                <div className="card-body">
                    <form autoComplete="off" className="form-flex">
                        {languages.map((language: LanguageResponse) => {
                            const translatedCategory = gameCategory.casino_game_category_translations?.find(
                                (translation: any) => translation.language_id === language.id
                            );

                            return (
                                <div key={language.code}>
                                    <FormGroupText
                                        name="name"
                                        key={`${language.id}_name`}
                                        label={`${language.name} name`}
                                        value={translatedCategory?.name ?? undefined}
                                        handleChange={handleTranslationChange}
                                        error={getTranslationError(language.id, 'name')}
                                        data-language-id={language.id}
                                        inputClasses="w-75"
                                    />
                                    <FormGroupText
                                        name="slug"
                                        key={`${language.id}_slug`}
                                        label={`${language.name} slug`}
                                        value={translatedCategory?.slug ?? undefined}
                                        handleChange={handleTranslationChange}
                                        error={getTranslationError(language.id, 'slug')}
                                        data-language-id={language.id}
                                        inputClasses="w-75"
                                    />
                                    <FormGroupText
                                        name="header"
                                        key={`${language.id}_header`}
                                        label={`${language.name} header`}
                                        value={translatedCategory?.header ?? undefined}
                                        handleChange={handleTranslationChange}
                                        error={getTranslationError(language.id, 'header')}
                                        data-language-id={language.id}
                                        inputClasses="w-75"
                                    />
                                </div>
                            );
                        })}
                        <Select
                            field="sorting_type"
                            value={gameCategory.sorting_type}
                            label="Sorting Type"
                            options={sortingTypeOptions}
                            handleChange={(event) => handleFieldChange('sorting_type', event.target.value)}
                            error={errors.sorting_type ?? undefined}
                            required
                        />
                        {gameCategory.is_all_games === false && (
                            <div>
                                <div className="form-group">
                                    <label htmlFor="allowed_types">Allowed types</label>
                                    <MultiSelect
                                        className="w-50"
                                        hasSelectAll={false}
                                        options={selectGameTypes}
                                        value={
                                            gameCategory.allowed_types?.map((type) => ({
                                                label: type,
                                                value: type,
                                            })) ?? []
                                        }
                                        onChange={handleMultiChange}
                                        labelledBy="allowed_types"
                                    />
                                </div>
                                <FormBoolean
                                    field="is_visible_on_homepage"
                                    label="Show on homepage"
                                    currentValue={gameCategory.is_visible_on_homepage}
                                    required
                                    error={errors.is_visible_on_homepage ?? undefined}
                                    handleChange={(event) => handleBooleanFieldChange('is_visible_on_homepage', event)}
                                />
                                <FormBoolean
                                    field="is_live_only"
                                    label="Allow only live games"
                                    currentValue={gameCategory.is_live_only}
                                    required
                                    error={errors.is_live_only ?? undefined}
                                    handleChange={(event) => handleBooleanFieldChange('is_live_only', event)}
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <button
                                onClick={() =>
                                    history.push({
                                        pathname: CASINO_GAME_CATEGORY,
                                    })
                                }
                                type="button"
                                className="btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <LoadingButton
                                isLoading={isSaving}
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleSave(gameCategory)}
                            >
                                Save
                            </LoadingButton>
                        </div>
                    </form>
                </div>
            </div>
        );
    };
    return <Loader loading={isLoading} content={getContent} />;
}

export default GameCategoryForm;
